import type { RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { getLanguage } from '@duke-hq/shared-constants/courseTranslationLanguages';
import {
  deriveTranslationStatus,
  type ContentTranslationUiStatus,
} from '@duke-hq/shared-constants/contentTranslationStatus';
import { statusBucket } from './coverageStatus';
import type { CoverageBarCounts } from './CoverageBar';

type CourseTranslations = RouterOutputs['contentTranslations']['listByCourse'];
type TranslationUnit = CourseTranslations['units'][number];
type TranslationResource = TranslationUnit['resources'][number];

// A single failed cell, carried to the bulk "Re-generate failed" action.
export type FailedCell = {
  resourceVersionId: number;
  resourceKind: 'PAGE' | 'CONVERSATION';
  language: string;
};

// Per-language rollup across a course's published resources. `withGaps` counts
// ready resources that are partly in English; they're inside `available`, not
// alongside it.
export type LanguageRollup = {
  code: string;
  englishName: string;
  endonym: string;
  rtl: boolean;
  // Published resources in the course; the same for every language.
  total: number;
  available: number;
  withGaps: number;
  pending: number;
  failed: number;
  stale: number;
  notTranslated: number;
};

// At most one row per (resource, language): unique on (sourceKind, sourceId, language).
function rowForLanguage(resource: TranslationResource, languageCode: string) {
  return resource.rows.find((row) => row.language === languageCode);
}

// One walk over every published resource x supported language, shared by the
// coverage page and the toolbar pill so the two can't disagree on a language's
// numbers. Also collects the cells a bulk retry needs.
export function buildLanguageRollups(
  units: readonly TranslationUnit[],
  supportedLanguages: readonly string[],
): { rollups: LanguageRollup[]; failedCells: FailedCell[] } {
  const byLanguage = new Map<string, LanguageRollup>();
  for (const code of supportedLanguages) {
    const language = getLanguage(code);
    byLanguage.set(code, {
      code,
      englishName: language?.englishName ?? code,
      endonym: language?.endonym ?? code,
      rtl: language?.rtl ?? false,
      total: 0,
      available: 0,
      withGaps: 0,
      pending: 0,
      failed: 0,
      stale: 0,
      notTranslated: 0,
    });
  }

  const failedCells: FailedCell[] = [];

  for (const unit of units) {
    for (const resource of unit.resources) {
      if (resource.currentPublishedVersionId === null) {
        continue;
      }
      const resourceVersionId = Number(resource.currentPublishedVersionId);
      for (const code of supportedLanguages) {
        const rollup = byLanguage.get(code);
        if (rollup === undefined) {
          continue;
        }
        rollup.total += 1;
        const status = deriveTranslationStatus(
          rowForLanguage(resource, code),
          resource.currentPublishedVersionId,
        );
        if (status === 'available-partial') {
          rollup.withGaps += 1;
        }
        switch (statusBucket(status)) {
          case 'ready':
            rollup.available += 1;
            break;
          case 'inProgress':
            rollup.pending += 1;
            break;
          case 'stale':
            rollup.stale += 1;
            break;
          case 'failed':
            rollup.failed += 1;
            failedCells.push({
              resourceVersionId,
              resourceKind: resource.resourceKind,
              language: code,
            });
            break;
          case 'notTranslated':
            rollup.notTranslated += 1;
            break;
        }
      }
    }
  }

  return { rollups: [...byLanguage.values()], failedCells };
}

export function rollupBarCounts(rollup: LanguageRollup): CoverageBarCounts {
  return {
    ready: rollup.available - rollup.withGaps,
    gaps: rollup.withGaps,
    inProgress: rollup.pending,
    attention: rollup.failed + rollup.stale,
    notStarted: rollup.notTranslated,
  };
}

// The one status a language's LED dot shows. In-flight outranks failure here,
// mirroring the course pill: while a language is still translating its dot
// breathes blue (the bar's red sliver carries the failure), and red waits until
// the language has settled. A resting untouched-or-partial language sits grey.
export function rollupDotStatus(rollup: LanguageRollup): ContentTranslationUiStatus {
  if (rollup.pending > 0) {
    return 'generating';
  }
  if (rollup.failed > 0) {
    return 'failed';
  }
  if (rollup.stale > 0) {
    return 'stale';
  }
  if (rollup.total > 0 && rollup.available === rollup.total) {
    return rollup.withGaps > 0 ? 'available-partial' : 'available';
  }
  return 'not-translated';
}
